import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

import {
  getConfigAuthValuesFromState,
  selectPickerStore,
  determineIsLoadingFromState,
  getOrdersFromState,
} from '../../../../redux/selectors';
import StyledAccordion from '../../../layout/StyledAccordion';

import useVerifyPickTour from '../../../../hooks/useVerifyPickTour';
import { getPickTourItems } from '../../../../redux/actions/activePickTour.actions';
import { getPickToursInProgress } from '../../../../redux/actions/orders.actions';
import { API_FETCH_PENDING, SHOULD_UPDATE_PICK_TOUR_SUB_STATUS, TRUE } from '../../../../constants';
import { getSortOrder } from '../../../../utils';
import { OrderType } from '../../../../types';
import { APIPickTour } from '../../../../redux/sagas/types';
import PickTourItem from './PickTourItem';
import styles from './ResetPickTour.css';

const ResetPickTour = () => {
  const dispatch = useDispatch();
  const [selectedPickTourIds, setSelectedPickTourIds] = useState<string[]>([]);
  const isLoading = useSelector(determineIsLoadingFromState);
  const ordersState = useSelector(getOrdersFromState);
  const pickerStore = useSelector(selectPickerStore);
  const configAuthValues = useSelector(getConfigAuthValuesFromState);
  const { storeID } = pickerStore;
  const verifyPickTour = useVerifyPickTour();
  const disablePickTour = verifyPickTour.status.status === API_FETCH_PENDING;

  const resumePickTour = (pickTourToResume: APIPickTour) => {
    const { pickTourID, pickTourType } = pickTourToResume;
    verifyPickTour.verify(
      storeID,
      configAuthValues,
      pickTourID,
      () => {
        localStorage.setItem(SHOULD_UPDATE_PICK_TOUR_SUB_STATUS, TRUE);
        const userData = JSON.parse(sessionStorage.getItem('userData') || '{}');
        const pickTourData = {
          ...pickTourToResume,
          assignedPickerEmployeeID: userData.sub || '',
        };
        dispatch(
          getPickTourItems({
            isPickTourCreation: true,
            page: 0,
            pickTourData,
            pickTourType,
          })
        );
      },
      () => dispatch(getPickToursInProgress({ fulfillmentStoreID: storeID || '' }))
    );
  };

  const { pickToursByType } = ordersState;

  useEffect(() => {
    if (isLoading) {
      setSelectedPickTourIds([]);
    }
  }, [isLoading]);

  const handlePickTourSelect = useCallback((pickTourId: string) => {
    return () => {
      setSelectedPickTourIds(prevSelected => {
        const isAlreadySelected = prevSelected.includes(pickTourId);
        if (isAlreadySelected) {
          return prevSelected.filter(id => id !== pickTourId);
        }
        return [...prevSelected, pickTourId];
      });
    };
  }, []);

  const isPickTourSelected = (pickTourId: string): boolean => {
    return selectedPickTourIds.includes(pickTourId);
  };

  const orderTypesWithPickTours = useMemo(() => {
    return (Object.keys(pickToursByType) as OrderType[])
      .sort((a, b) => getSortOrder(a, pickToursByType) - getSortOrder(b, pickToursByType))
      .filter(orderType => {
        const orderGroup = pickToursByType[orderType];
        return orderGroup?.pickTours?.length;
      });
  }, [pickToursByType]);

  const hasPickTours = orderTypesWithPickTours.length > 0;
  const { t } = useTranslation();

  if (hasPickTours) {
    return (
      <div className={styles.mainContent}>
        {orderTypesWithPickTours.map(group => {
          const { pickTours = [] } = pickToursByType[group] || {};
          return (
            <StyledAccordion
              key={group}
              header={
                <h2 className={styles.accordionHeader}>
                  {t(`UTILS.${group}`)}
                  <span className={styles.headerCount}>({pickTours.length})</span>
                </h2>
              }
              defaultExpanded
            >
              <ul>
                {pickTours.map((pickTour, pickTourIndex) => (
                  <PickTourItem
                    key={pickTour.pickTourID}
                    pickTour={pickTour}
                    pickTourIndex={pickTourIndex}
                    isSelected={isPickTourSelected(pickTour.pickTourID)}
                    isDisabled={disablePickTour}
                    onSelect={handlePickTourSelect(pickTour.pickTourID)}
                    resumePickTour={resumePickTour}
                  />
                ))}
              </ul>
            </StyledAccordion>
          );
        })}
      </div>
    );
  }

  if (isLoading) return null;

  return (
    <article className={styles.noOrdersContainer}>
      <ErrorOutlineIcon className={styles.noResultsIcon} />
      <h2>{t('ORDER_LIST.NO_RESULTS')}</h2>
      <p>{t('RESUME_PICK_TOURS.NO_ACTIVE_TOURS')}</p>
    </article>
  );
};

export default ResetPickTour;
